import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export interface UserInfo {
  uid: string;
  email: string | null;
  displayName: string | null;
  createdAt?: string;
  hasData?: boolean;
}

export interface MigrationInfo {
  migrated: boolean;
  migratedAt?: string;
  programCount?: number;
  source?: string;
}

export interface UserMigrationStatus {
  userInfo: UserInfo;
  migrationStatus: MigrationInfo | null;
}

export interface MigrationResult {
  success: boolean;
  message: string;
  userInfo: UserInfo;
  migrationInfo?: MigrationInfo;
}

export interface UserSearchResult {
  users: UserInfo[];
  totalFound: number;
  searchQuery: string;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface BatchInfo {
  currentBatch: number;
  hasNextBatch: boolean;
  hasPreviousBatch: boolean;
}

export interface UserDataStatus {
  hasData: boolean;
  reasons: string[];
  details: {
    hasProfile: boolean;
    hasCourses: boolean;
    hasGrades: boolean;
    courseCount: number;
    gradeCount: number;
  };
}

export interface EnhancedUserInfo extends UserInfo {
  isMigrated: boolean;
  dataStatus: UserDataStatus;
}

export interface UsersReadyForMigrationResult {
  users: EnhancedUserInfo[];
  pagination: PaginationInfo;
  batch: BatchInfo;
  status: string;
}

class AdminMigrationService {
  private adminMigrateUserCall = httpsCallable(functions, 'adminMigrateUser');
  private adminGetUserMigrationStatusCall = httpsCallable(functions, 'adminGetUserMigrationStatus');
  private adminSearchUsersCall = httpsCallable(functions, 'adminSearchUsers');
  private adminGetUsersReadyForMigrationCall = httpsCallable(functions, 'adminGetUsersReadyForMigration');

  /**
   * Get paginated list of users with migration status filtering
   */
  async getUsersReadyForMigration(page: number = 1, limit: number = 10, status: 'ready' | 'not_ready' | 'all' = 'ready', batch: number = 1): Promise<UsersReadyForMigrationResult> {
    try {
      const result = await this.adminGetUsersReadyForMigrationCall({
        page,
        limit,
        status,
        batch
      });
      return result.data as UsersReadyForMigrationResult;
    } catch (error: any) {
      console.error('Error getting users ready for migration:', error);
      throw new Error(error.message || 'Failed to get users ready for migration');
    }
  }

  /**
   * Search for users by email or display name
   */
  async searchUsers(searchQuery: string, limit: number = 20): Promise<UserSearchResult> {
    try {
      const result = await this.adminSearchUsersCall({
        searchQuery: searchQuery.trim(),
        limit
      });
      return result.data as UserSearchResult;
    } catch (error: any) {
      console.error('Error searching users:', error);
      throw new Error(error.message || 'Failed to search users');
    }
  }

  /**
   * Get migration status for a specific user
   */
  async getUserMigrationStatus(userId: string): Promise<UserMigrationStatus> {
    try {
      const result = await this.adminGetUserMigrationStatusCall({
        targetUserId: userId
      });
      return result.data as UserMigrationStatus;
    } catch (error: any) {
      console.error('Error getting user migration status:', error);
      throw new Error(error.message || 'Failed to get user migration status');
    }
  }

  /**
   * Migrate a specific user's eligibility data
   */
  async migrateUser(userId: string): Promise<MigrationResult> {
    try {
      console.log(`🔄 Starting admin migration for user: ${userId}`);
      const result = await this.adminMigrateUserCall({
        targetUserId: userId
      });
      return result.data as MigrationResult;
    } catch (error: any) {
      console.error('Error migrating user:', error);
      throw new Error(error.message || 'Failed to migrate user');
    }
  }

  /**
   * Get user info and migration status in one call
   */
  async getUserInfoAndStatus(userId: string): Promise<UserMigrationStatus> {
    return this.getUserMigrationStatus(userId);
  }

  /**
   * Check if a user can be migrated (has sufficient data)
   */
  async canUserBeMigrated(userId: string): Promise<boolean> {
    try {
      const status = await this.getUserMigrationStatus(userId);
      return status.userInfo.hasData || false;
    } catch (error) {
      console.error('Error checking if user can be migrated:', error);
      return false;
    }
  }

  /**
   * Check if a user is already migrated
   */
  async isUserMigrated(userId: string): Promise<boolean> {
    try {
      const status = await this.getUserMigrationStatus(userId);
      return status.migrationStatus?.migrated || false;
    } catch (error) {
      console.error('Error checking if user is migrated:', error);
      return false;
    }
  }
}

export const adminMigrationService = new AdminMigrationService();
